import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Menu, X, LogOut, User, Bell, MessageCircle } from "lucide-react";
import HostroLogo from "./HostroLogo";

export default function Navbar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);
  const unread = notifs.filter(n => !n.read).length;

  useEffect(() => {
    if (!(user && typeof user === "object")) {
      setNotifs([]);
      return;
    }
    const fetchN = () => api.get("/notifications").then(r => setNotifs(r.data)).catch(()=>{});
    fetchN();
    const t = setInterval(fetchN, 30000);
    return () => clearInterval(t);
  }, [user]);

  const markAllRead = async () => {
    await api.patch("/notifications/read-all").catch(()=>{});
    setNotifs(notifs.map(n => ({...n, read: true})));
  };

  const link = "px-3 py-2 font-bold text-sm uppercase tracking-wider hover:underline underline-offset-4 decoration-2";

  return (
    <header className="bg-[#FDFCF8] border-b-2 border-[#0F1F17] sticky top-0 z-40" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="logo-home">
          <HostroLogo size={36} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          <NavLink to="/properties" className={link} data-testid="nav-properties">Find PGs</NavLink>
          <NavLink to="/roommates" className={link} data-testid="nav-roommates">Roommates</NavLink>
          <NavLink to="/student-ambassador" className={link} data-testid="nav-ambassador">Ambassador</NavLink>
          <NavLink to="/list-property" className={link} data-testid="nav-list-property">List Your PG</NavLink>
          <NavLink to="/about" className={link} data-testid="nav-about">About</NavLink>
          <NavLink to="/blog" className={link} data-testid="nav-blog">Blog</NavLink>
          <NavLink to="/contact" className={link} data-testid="nav-contact">Contact</NavLink>
        </nav>

        <div className="hidden md:flex items-center gap-3 relative">
          {user && typeof user === "object" ? (
            <>
              <div className="relative">
                <button onClick={()=>setBellOpen(!bellOpen)} className="brut-border p-2 bg-white relative" data-testid="bell-btn">
                  <Bell size={16}/>
                  {unread > 0 && <span className="absolute -top-2 -right-2 brut-tag pink !px-1.5 !py-0.5" data-testid="bell-count">{unread}</span>}
                </button>
                {bellOpen && (
                  <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto brut-card bg-white" data-testid="notif-panel">
                    <div className="p-3 border-b-2 border-[#0F1F17] flex justify-between items-center">
                      <strong>Notifications</strong>
                      {unread > 0 && <button onClick={markAllRead} className="text-xs underline" data-testid="mark-all-read">Mark all read</button>}
                    </div>
                    {notifs.length === 0 ? <div className="p-4 text-sm text-[#4A4A4A]">No notifications yet.</div>
                      : notifs.map(n => (
                        <div key={n.id} className={`p-3 border-b border-[#0F1F17]/30 ${!n.read ? "bg-[#FFEAF6]" : ""}`}>
                          <div className="font-bold text-sm">{n.title}</div>
                          <div className="text-xs text-[#4A4A4A]">{n.body}</div>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
              {user.role === "student" && <Link to="/laundry" className="text-sm font-bold text-[var(--ink)] hidden lg:inline-flex items-center" data-testid="nav-laundry">Laundry</Link>}
              {user.role === "student" && <Link to="/orders" className="text-sm font-bold text-[var(--ink)] hidden lg:inline-flex items-center" data-testid="nav-orders">Orders</Link>}
              <Link to="/messages" className="brut-border p-2 bg-white" data-testid="nav-messages" aria-label="Messages"><MessageCircle size={16}/></Link>
              {user.role === "owner" && <Link to="/operator" className="brut-btn pink !py-2 !px-3" data-testid="nav-operator">Operator</Link>}
              {user.role === "admin" && <Link to="/admin" className="brut-btn pink !py-2 !px-3" data-testid="nav-admin">Admin</Link>}
              {user.role === "super_admin" && <Link to="/super-admin" className="brut-btn pink !py-2 !px-3" data-testid="nav-super-admin">Mission Control</Link>}
              {user.role === "staff" && <Link to="/staff" className="brut-btn pink !py-2 !px-3" data-testid="nav-staff">Staff · {user.department}</Link>}
              {user.role === "vendor" && <Link to="/vendors/dashboard" className="brut-btn pink !py-2 !px-3" data-testid="nav-vendor-dash">Vendor</Link>}
              <Link to="/dashboard" className="brut-btn white !py-2 !px-3" data-testid="nav-dashboard">
                <User size={16}/> {user.name?.split(" ")[0]}
              </Link>
              <button className="brut-btn pink !py-2 !px-3" onClick={()=>navigate("/logout")} data-testid="nav-logout">
                <LogOut size={16}/>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="brut-btn white !py-2 !px-3" data-testid="nav-login">Sign in</Link>
              <Link to="/register" className="brut-btn !py-2 !px-3" data-testid="nav-register">Get Started</Link>
            </>
          )}
        </div>

        <button className="lg:hidden brut-border p-2" onClick={()=>setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="menu">
          {open ? <X size={20}/> : <Menu size={20}/>}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t-2 border-[#0F1F17] bg-[#FDFCF8] p-4 flex flex-col gap-2" data-testid="nav-mobile-menu">
          <NavLink to="/properties" className={link} onClick={()=>setOpen(false)}>Find PGs</NavLink>
          <NavLink to="/vendors" className={link} onClick={()=>setOpen(false)}>Vendors</NavLink>
          <NavLink to="/list-property" className={link} onClick={()=>setOpen(false)}>List Your PG</NavLink>
          <NavLink to="/about" className={link} onClick={()=>setOpen(false)}>About</NavLink>
          <NavLink to="/blog" className={link} onClick={()=>setOpen(false)}>Blog</NavLink>
          <NavLink to="/faq" className={link} onClick={()=>setOpen(false)}>FAQ</NavLink>
          <NavLink to="/contact" className={link} onClick={()=>setOpen(false)}>Contact</NavLink>
          {user && typeof user === "object" ? (
            <>
              {user.role === "admin" && <Link to="/admin" className="brut-btn pink" onClick={()=>setOpen(false)}>Admin Panel</Link>}
              {user.role === "super_admin" && <Link to="/super-admin" className="brut-btn pink" onClick={()=>setOpen(false)}>Mission Control</Link>}
              {user.role === "staff" && <Link to="/staff" className="brut-btn pink" onClick={()=>setOpen(false)}>Staff Dashboard</Link>}
              <Link to="/dashboard" className="brut-btn white" onClick={()=>setOpen(false)}>Dashboard</Link>
              <button className="brut-btn pink" onClick={()=>{setOpen(false); navigate("/logout");}}>Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="brut-btn white" onClick={()=>setOpen(false)}>Sign in</Link>
              <Link to="/register" className="brut-btn" onClick={()=>setOpen(false)}>Get Started</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
